/**
 * useCreateFormState Hook
 * Manages form state: steps, date, edit states
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { ScrollView } from 'react-native';
import { useBuildViewPrefStore } from '../stores/buildViewPrefStore';
import type { BuildView, CreateStep, EditState } from '../types/create.types';

interface UseCreateFormStateReturn {
  step: CreateStep;
  setStep: React.Dispatch<React.SetStateAction<CreateStep>>;
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  editStates: Record<string, EditState>;
  setEditStates: React.Dispatch<React.SetStateAction<Record<string, EditState>>>;
  stakeCoins: number;
  setStakeCoins: React.Dispatch<React.SetStateAction<number>>;
  /** Build screen sub-view: 'deck' (one at a time, default) or 'list' (all games). */
  buildView: BuildView;
  setBuildView: React.Dispatch<React.SetStateAction<BuildView>>;
  /** Current deck card index (rival-first "one at a time" view). */
  deckIdx: number;
  setDeckIdx: React.Dispatch<React.SetStateAction<number>>;
  scrollViewRef: React.RefObject<ScrollView | null>;
  resetForm: () => void;
}

export const useCreateFormState = (initialStep?: CreateStep): UseCreateFormStateReturn => {
  const [step, setStep] = useState<CreateStep>(initialStep ?? 'rival');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [editStates, setEditStates] = useState<Record<string, EditState>>({});
  const [stakeCoins, setStakeCoins] = useState<number>(0);
  // Build view opens on the last view the user had open (persisted; T-53). getState() is safe here:
  // the store hydrates synchronously on web and at app start on native, long before create mounts.
  const [buildView, setBuildViewState] = useState<BuildView>(
    () => useBuildViewPrefStore.getState().preferredBuildView
  );
  const [deckIdx, setDeckIdx] = useState<number>(0);
  const scrollViewRef = useRef<ScrollView>(null);

  // Every view change records the preference — the toggle itself is the "default view" setting.
  const setBuildView = useCallback<React.Dispatch<React.SetStateAction<BuildView>>>((action) => {
    setBuildViewState((prev) => {
      const next = typeof action === 'function' ? action(prev) : action;
      useBuildViewPrefStore.getState().setPreferredBuildView(next);
      return next;
    });
  }, []);

  // Reset to top when the user switches date in the build list view.
  // (The list view is the only screen whose ScrollView carries scrollViewRef.)
  useEffect(() => {
    if (step !== 'build' || buildView !== 'list') return;
    scrollViewRef.current?.scrollTo({ y: 0, animated: false });
  }, [selectedDate, step, buildView]);

  const resetForm = useCallback(() => {
    setStep('rival');
    setEditStates({});
    setStakeCoins(0);
    // Programmatic reset must NOT clobber the persisted preference — restore the preferred view.
    setBuildViewState(useBuildViewPrefStore.getState().preferredBuildView);
    setDeckIdx(0);
  }, []);

  return {
    step,
    setStep,
    selectedDate,
    setSelectedDate,
    editStates,
    setEditStates,
    stakeCoins,
    setStakeCoins,
    buildView,
    setBuildView,
    deckIdx,
    setDeckIdx,
    scrollViewRef,
    resetForm,
  };
};

